import { useState, useEffect, createContext, useContext } from 'react';
import axios from 'axios';
import { AlertTriangle, XCircle, Clock, Key } from 'lucide-react';

const API = '/api';

// License Context
const LicenseContext = createContext(null);

export function useLicense() {
  return useContext(LicenseContext);
}

export function LicenseProvider({ children }) {
  const [licenseStatus, setLicenseStatus] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    checkLicense();
  }, []);

  const checkLicense = async () => {
    try {
      const res = await axios.get(`${API}/license`);
      setLicenseStatus(res.data);
    } catch (error) {
      console.error('Failed to check license:', error);
      setLicenseStatus({ status: 'error', is_valid: true, show_warning: false });
    } finally {
      setLoading(false);
    }
  };

  const refreshLicense = () => {
    checkLicense();
  };

  return (
    <LicenseContext.Provider value={{ licenseStatus, loading, refreshLicense }}>
      {children}
    </LicenseContext.Provider>
  );
}

// License Warning Banner Component
export function LicenseWarningBanner() {
  const { licenseStatus, loading } = useLicense();
  const [dismissed, setDismissed] = useState(false);

  if (loading || dismissed) return null;
  if (!licenseStatus) return null;

  // Show nothing if license is valid and not expiring soon
  if (licenseStatus.is_valid && !licenseStatus.show_warning) return null;

  const isExpired = licenseStatus.is_expired;
  const daysRemaining = licenseStatus.days_remaining;

  return (
    <div 
      className={`px-4 py-3 flex items-center justify-between ${
        isExpired 
          ? 'bg-red-600 text-white' 
          : 'bg-amber-500 text-amber-950'
      }`}
      data-testid="license-warning-banner"
    >
      <div className="flex items-center gap-3">
        {isExpired ? (
          <XCircle size={20} />
        ) : (
          <AlertTriangle size={20} />
        )}
        <span className="font-medium">
          {isExpired ? (
            <>
              Your license has expired. Please renew to continue using all features.
              <a href="/settings" className="ml-2 underline font-bold">Renew Now</a>
            </>
          ) : (
            <>
              Your license expires in {daysRemaining} day{daysRemaining !== 1 ? 's' : ''}.
              <a href="/settings" className="ml-2 underline font-bold">Renew Now</a>
            </>
          )}
        </span>
      </div>
      {!isExpired && (
        <button 
          onClick={() => setDismissed(true)}
          className="text-amber-950 hover:text-amber-800 font-bold"
        >
          Dismiss
        </button>
      )}
    </div>
  );
}

// License Status Card for Settings Page
export function LicenseStatusCard({ onRefresh }) {
  const { licenseStatus, loading, refreshLicense } = useLicense();
  const [activating, setActivating] = useState(false);
  const [licenseKey, setLicenseKey] = useState('');
  const [ownerName, setOwnerName] = useState('');
  const [ownerEmail, setOwnerEmail] = useState('');
  const [message, setMessage] = useState(null);

  const handleActivate = async (e) => {
    e.preventDefault();
    if (!licenseKey.trim()) {
      setMessage({ type: 'error', text: 'Please enter a license key' });
      return;
    }

    setActivating(true);
    setMessage(null);

    try {
      const token = localStorage.getItem('token');
      const res = await axios.post(`${API}/license/activate`, {
        license_key: licenseKey.trim().toUpperCase(),
        owner_name: ownerName,
        owner_email: ownerEmail
      }, {
        headers: token ? { Authorization: `Bearer ${token}` } : {}
      });
      
      setMessage({ type: 'success', text: res.data.message });
      setLicenseKey('');
      refreshLicense();
      if (onRefresh) onRefresh();
    } catch (error) {
      setMessage({ 
        type: 'error', 
        text: error.response?.data?.detail || 'Failed to activate license' 
      });
    } finally {
      setActivating(false);
    }
  };

  if (loading) {
    return (
      <div className="p-6 bg-slate-50 rounded-lg">
        <p className="text-slate-500">Loading license status...</p>
      </div>
    );
  }

  const status = licenseStatus || { status: 'no_license' };
  const isExpired = status.is_expired;
  const showWarning = status.show_warning;
  const hasLicense = status.status !== 'no_license' && status.status !== 'error';

  return (
    <div className="space-y-6">
      {/* Current License Status */}
      <div className={`p-6 rounded-lg border-2 ${
        isExpired 
          ? 'bg-red-50 border-red-300' 
          : showWarning 
            ? 'bg-amber-50 border-amber-300'
            : hasLicense 
              ? 'bg-green-50 border-green-300'
              : 'bg-slate-50 border-slate-200'
      }`}>
        <div className="flex items-start justify-between">
          <div>
            <h3 className={`font-semibold text-lg flex items-center gap-2 ${
              isExpired ? 'text-red-700' : showWarning ? 'text-amber-700' : hasLicense ? 'text-green-700' : 'text-slate-700'
            }`}>
              {isExpired ? (
                <><XCircle size={20} /> License Expired</>
              ) : showWarning ? (
                <><Clock size={20} /> License Expiring Soon</>
              ) : hasLicense ? (
                <><Key size={20} /> License Active</>
              ) : (
                <><Key size={20} /> No License</>
              )}
            </h3>
            
            {hasLicense && (
              <div className="mt-3 space-y-1 text-sm">
                <p><span className="text-slate-500">Type:</span> <strong>{status.license_type}</strong></p>
                <p><span className="text-slate-500">Key:</span> <code className="bg-white px-2 py-0.5 rounded">{status.license_key}</code></p>
                <p><span className="text-slate-500">Expires:</span> <strong>{status.expires_date}</strong></p>
                {!isExpired && (
                  <p><span className="text-slate-500">Days Remaining:</span> <strong className={showWarning ? 'text-amber-600' : ''}>{status.days_remaining}</strong></p>
                )}
                <p><span className="text-slate-500">Max Users:</span> <strong>{status.max_users?.toLocaleString()}</strong></p>
                <p><span className="text-slate-500">Max Events:</span> <strong>{status.max_events?.toLocaleString()}</strong></p>
                {status.owner && <p><span className="text-slate-500">Owner:</span> <strong>{status.owner}</strong></p>}
              </div>
            )}
            
            {!hasLicense && (
              <p className="mt-2 text-slate-600">Enter a license key below to activate your installation.</p>
            )}
          </div>
          
          {(isExpired || showWarning) && (
            <span className={`px-3 py-1 rounded-full text-xs font-bold ${
              isExpired ? 'bg-red-200 text-red-800' : 'bg-amber-200 text-amber-800'
            }`}>
              {isExpired ? 'EXPIRED' : `${status.days_remaining} DAYS LEFT`}
            </span>
          )}
        </div>
      </div>

      {/* Activate/Renew License Form */}
      <div className="p-6 bg-slate-50 rounded-lg">
        <h4 className="font-semibold mb-4 flex items-center gap-2">
          <Key size={18} className="text-[#1B3B6F]" />
          {hasLicense ? 'Renew License' : 'Activate License'}
        </h4>
        
        <form onSubmit={handleActivate} className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-slate-700 mb-1">License Key</label>
            <input
              type="text"
              value={licenseKey}
              onChange={(e) => setLicenseKey(e.target.value.toUpperCase())}
              placeholder="PRO-XXXX-XXXX-XXXX-XXXXXXXX"
              className="w-full px-4 py-2 border rounded-lg font-mono text-sm focus:ring-2 focus:ring-[#1B3B6F] focus:border-transparent"
              data-testid="license-key-input"
            />
          </div>
          
          <div className="grid md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1">Owner Name (optional)</label>
              <input
                type="text"
                value={ownerName}
                onChange={(e) => setOwnerName(e.target.value)}
                placeholder="Your Name"
                className="w-full px-4 py-2 border rounded-lg text-sm focus:ring-2 focus:ring-[#1B3B6F] focus:border-transparent"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1">Owner Email (optional)</label>
              <input
                type="email"
                value={ownerEmail}
                onChange={(e) => setOwnerEmail(e.target.value)}
                placeholder="your@email.com"
                className="w-full px-4 py-2 border rounded-lg text-sm focus:ring-2 focus:ring-[#1B3B6F] focus:border-transparent"
              />
            </div>
          </div>
          
          {message && (
            <div className={`p-3 rounded-lg text-sm ${
              message.type === 'success' ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-700'
            }`}>
              {message.text}
            </div>
          )}
          
          <button
            type="submit"
            disabled={activating}
            className="w-full bg-[#1B3B6F] text-white py-2 px-4 rounded-lg font-medium hover:bg-[#1B3B6F]/90 disabled:opacity-50"
            data-testid="activate-license-btn"
          >
            {activating ? 'Activating...' : (hasLicense ? 'Renew License' : 'Activate License')}
          </button>
        </form>
        
        <p className="mt-4 text-xs text-slate-500">
          Need a license key? Contact your administrator or purchase one from the vendor.
        </p>
      </div>
    </div>
  );
}

export default LicenseWarningBanner;
